// Asking the messenger for the user's phone number — as a *convenience only*.
//
// All three SDKs expose requestContact(), which shows the client's own native
// "share your number" sheet and hands the number back to this page.
//
// The platforms answer in two different shapes, and the difference decides
// whether the number can be trusted:
//
// Eitaa / Telegram
//     callback(sent, event) where `event.response` is a SEPARATELY SIGNED
//     payload — `contact=<json>&auth_date=…&hash=…`, signed with the same bot
//     token as initData. The backend verifies that signature, so the number is
//     proven and the user can be signed straight in. No SMS.
//
// Bale
//     callback(sent, "09121234567") — a bare string with no signature at all.
//     The page could have made it up, and so could anyone who opens the site
//     outside a messenger. So on Bale it may ONLY pre-fill the login field;
//     the SMS code still does the proving. Bale users get an equivalent
//     zero-SMS path through the bot's shared-contact button instead.

import posthog from 'posthog-js'

// `toLocalMobile` lives in `utilities/phone` now: the login field is no longer
// the only thing that needs one spelling of a phone number — analytics keys its
// person on it too, and the two must agree or the same user becomes two people.
import { toLocalMobile } from 'utilities/phone'
import { resolvePlatform } from './detect'
import { loadSdk } from './sdk'

export { toLocalMobile } from 'utilities/phone'

const REQUEST_TIMEOUT_MS = 60000

/** True when this client can offer the native number-sharing sheet at all. */
export const canRequestContact = async (): Promise<boolean> => {
    const platform = resolvePlatform()
    if (!platform) return false
    const webApp = await loadSdk(platform)
    return typeof webApp?.requestContact === 'function'
}

export interface ContactResult {
    /** Signed `contact=…&auth_date=…&hash=…` payload; verifiable by the backend. */
    signedResponse?: string
    /** Unsigned local-format number. Pre-fill only — never proof. */
    phone?: string
}

/**
 * Show the native sheet and resolve with whatever the client gives us.
 *
 * Resolves to null if the user declined, the client refused, or it never
 * answered. Bale caps this at three requests per session and then throws, and
 * Eitaa throws if a request is already in flight — both are caught here, which
 * is why the manual field must always stay usable.
 */
export const requestPhoneFromMessenger = async (): Promise<ContactResult | null> => {
    const platform = resolvePlatform()
    if (!platform) return null
    const webApp = await loadSdk(platform)
    const request = webApp?.requestContact
    if (!webApp || !request) return null

    return new Promise<ContactResult | null>((resolve) => {
        let settled = false
        const finish = (value: ContactResult | null) => {
            if (settled) return
            settled = true
            resolve(value)
        }
        try {
            request.call(webApp, (shared: boolean, payload?: unknown) => {
                // Wrapped: this callback runs asynchronously, so anything that
                // throws in here would otherwise leave the promise pending
                // forever and the button spinning — which is exactly what an
                // earlier version did on Eitaa by assuming a string.
                try {
                    if (!shared) {
                        posthog.capture('miniapp_request_contact_not_shared', {
                            platform,
                        })
                        return finish(null)
                    }

                    if (typeof payload === 'string') {
                        return finish({ phone: toLocalMobile(payload) || undefined })
                    }
                    const event = (payload || {}) as {
                        response?: string
                        responseUnsafe?: { contact?: { phone_number?: string } }
                    }
                    // Distinguishes "user shared but the client sent us
                    // nothing" from "the client refused outright".
                    posthog.capture('miniapp_request_contact_shared', {
                        platform,
                        has_signed_response: !!event.response,
                        has_parsed_contact: !!event.responseUnsafe?.contact,
                    })
                    finish({
                        signedResponse: event.response,
                        phone:
                            toLocalMobile(
                                event.responseUnsafe?.contact?.phone_number
                            ) || undefined,
                    })
                } catch {
                    finish(null)
                }
            })
        } catch (error) {
            // The SDKs throw for several distinct reasons — unsupported client
            // version, a request already in flight, a per-session cap — and
            // they all look identical to the user. Record which, so a platform
            // bug can be reported with the actual message rather than a guess.
            posthog.capture('miniapp_request_contact_threw', {
                platform,
                message: String((error as Error)?.message || error).slice(0, 200),
            })
            finish(null)
        }
        // The sheet can be dismissed in ways that never fire the callback.
        window.setTimeout(() => finish(null), REQUEST_TIMEOUT_MS)
    })
}
